import { Link } from '@tanstack/react-router';

import type { PracticeCategoryKey } from '@/lib/api/lms';
import type { ReadinessComponentResult, ReadinessPillarResult } from '@/lib/api/readiness';
import { useHome } from '@/lib/hooks/useHome';
import { overallWeight, weightPct } from '@/features/Readiness';

type Destination =
  | { kind: 'course' }
  | { kind: 'practiceHub' }
  | { kind: 'practiceCategory'; category: PracticeCategoryKey }
  | { kind: 'schedule' };

/**
 * Where to send a student for each scored component, and what the button says.
 *
 * Deliberately no weights and no labels here: both come from the API. The old
 * version hardcoded "30% / 25% / 15% / 15% / 15%" into the copy, which was
 * both duplicated and — after the v2 formula — wrong.
 */
const ACTIONS: Record<string, { cta: string; dest: Destination }> = {
  coding_solve_rate: { cta: 'Open coding problems', dest: { kind: 'practiceCategory', category: 'problems' } },
  assessment_score: { cta: 'Review your quizzes', dest: { kind: 'practiceCategory', category: 'quizzes' } },
  project_score: { cta: 'Open your projects', dest: { kind: 'practiceCategory', category: 'projects' } },
  skill_coverage: { cta: 'Practise a new topic', dest: { kind: 'practiceHub' } },
  attendance: { cta: 'See your schedule', dest: { kind: 'schedule' } },
  on_time: { cta: 'Open assignments', dest: { kind: 'practiceCategory', category: 'assignments' } },
  completion: { cta: 'Continue your course', dest: { kind: 'course' } },
};

const FALLBACK_ACTION = { cta: 'Open practice', dest: { kind: 'practiceHub' } as Destination };

interface Candidate {
  component: ReadinessComponentResult;
  /** Score points recoverable by taking this component to 100. */
  gain: number;
  /** Share of the whole score this component currently carries, 0..1. */
  share: number;
}

/** The measured component where closing the gap moves the score most. */
function biggestLever(pillars: ReadinessPillarResult[]): Candidate | null {
  const candidates: Candidate[] = [];
  pillars.forEach((pillar: ReadinessPillarResult) => {
    if (pillar.excluded) return;
    pillar.components.forEach((component) => {
      if (component.excluded || component.value === null) return;
      const share = overallWeight(pillar, component);
      candidates.push({ component, share, gain: (100 - component.value) * share });
    });
  });
  if (candidates.length === 0) return null;
  return candidates.reduce((best, c) => (c.gain > best.gain ? c : best));
}

/** The heaviest component that has nothing to measure yet, if any. */
function biggestUnmeasured(pillars: ReadinessPillarResult[]): ReadinessComponentResult | null {
  const missing: { component: ReadinessComponentResult; nominal: number }[] = [];
  pillars.forEach((pillar) => {
    pillar.components.forEach((component) => {
      if (component.excluded || component.value === null) {
        missing.push({ component, nominal: pillar.weight * component.weight });
      }
    });
  });
  if (missing.length === 0) return null;
  return missing.reduce((best, m) => (m.nominal > best.nominal ? m : best)).component;
}

const BUTTON_CLASS =
  'inline-flex items-center rounded-lg bg-foreground px-3.5 py-2 text-xs font-semibold text-background';

export function BiggestLeverCard({ batchId }: { batchId: string }) {
  // Shares the query cache with ScoreBreakdown — no extra request. Weights are
  // read off the breakdown the server already applied, never restated here.
  const { data: home } = useHome(batchId);

  const breakdown = home?.readiness?.breakdown;
  if (!breakdown || !breakdown.has_data) {
    // With nothing measured, "your weakest component is X" is meaningless.
    return (
      <section aria-label="Biggest lever">
        <p className="text-[10.5px] font-bold uppercase tracking-wider text-primary">Biggest lever</p>
        <p className="mt-2.5 text-[13.5px] text-foreground">
          Nothing has been measured yet. Finish your <strong>first piece of graded work</strong> to
          get a real readiness score.
        </p>
        <div className="mt-3.5">
          <Link to="/courses/$batchId/practice" params={{ batchId }} className={BUTTON_CLASS}>
            Open practice
          </Link>
        </div>
      </section>
    );
  }

  const lever = biggestLever(breakdown.pillars);
  const unmeasured = biggestUnmeasured(breakdown.pillars);
  const action = (lever && ACTIONS[lever.component.key]) ?? FALLBACK_ACTION;

  return (
    <section aria-label="Biggest lever">
      <p className="text-[10.5px] font-bold uppercase tracking-wider text-primary">Biggest lever</p>

      {lever && lever.gain >= 1 ? (
        <p className="mt-2.5 text-[13.5px] text-foreground">
          <strong>
            {lever.component.label} ({Math.round(lever.component.value ?? 0)})
          </strong>{' '}
          is costing you the most — it counts for {weightPct(lever.share)} of your score right now.
        </p>
      ) : (
        <p className="mt-2.5 text-[13.5px] text-foreground">
          You are close to the ceiling on everything measured so far — keep it steady.
        </p>
      )}

      {unmeasured && (
        <p className="mt-1.5 text-xs text-muted-foreground">
          {unmeasured.label} isn&apos;t scored yet — there is nothing to measure, so it is neither
          helping nor hurting you.
        </p>
      )}

      <div className="mt-3.5">
        {action.dest.kind === 'course' && (
          <Link to="/courses/$batchId" params={{ batchId }} className={BUTTON_CLASS}>
            {action.cta}
          </Link>
        )}
        {action.dest.kind === 'practiceHub' && (
          <Link to="/courses/$batchId/practice" params={{ batchId }} className={BUTTON_CLASS}>
            {action.cta}
          </Link>
        )}
        {action.dest.kind === 'practiceCategory' && (
          <Link
            to="/courses/$batchId/practice/$category"
            params={{ batchId, category: action.dest.category }}
            className={BUTTON_CLASS}
          >
            {action.cta}
          </Link>
        )}
        {action.dest.kind === 'schedule' && (
          <Link to="/schedule" className={BUTTON_CLASS}>
            {action.cta}
          </Link>
        )}
      </div>
    </section>
  );
}
